import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { PrimaryButton, SecondaryButton } from '../../styledComponents/buttons';
import { defaultApi } from '../../utils/axiosApi';
import ComponentCircleLoader from '../../components/Loaders/ComponentCircleLoader';
import Select from '../../components/ReactSelectDropdown';
import { filterOption } from '../../components/ReactSelectDropdown/ReactSelectDropdown';
import CreateProjectDropdown from '../../components/DropdownStyle/CreateProjectDropdown';
import ClearFilterButton from '../../components/Search/clearFilterButton';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.main.white};
  border-radius: ${({ theme }) => theme.borderRadii.card};
  padding: 30px;
  max-width: 900px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.textMain};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.main.textMain};
  }
`;

const EmployeeSection = styled.div`
  margin-bottom: 25px;
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
  border-radius: ${({ theme }) => theme.borderRadii.card};
  padding: 15px;
`;

const EmployeeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const EmployeeName = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.textMain};
  margin: 0;
`;

const SelectAllButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.main.primary};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    opacity: 0.8;
  }
`;

const JobsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const JobItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.main.whiteBg};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const JobInfo = styled.div`
  flex: 1;
`;

const JobName = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.main.textMain};
`;

const JobAddress = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  margin-top: 2px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
`;

const AssigneeSelectionSection = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.textMain};
  margin-bottom: 8px;
  display: block;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
`;

const SelectionSummary = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.main.textMain};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const RoutePointsSection = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background-color: ${({ theme }) => theme.colors.main.whiteBg};
  border-radius: ${({ theme }) => theme.borderRadii.card};
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
`;

const RoutePointsTitle = styled.h3`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.textMain};
  margin: 0 0 12px 0;
`;

const RoutePointsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

const RoutePointField = styled.div`
  display: flex;
  flex-direction: column;
`;

const RoutePointLabel = styled.label`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.main.textMain};
  margin-bottom: 6px;
`;

const RoutePointHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  margin-top: 4px;
`;

function JobSelectionModal({ organizationId, selectedDate, onClose, onOptimize, organization }) {
  // Call this at the top to ensure hooks are always called in the same order
  const dropdownStyles = CreateProjectDropdown();
  const orgType = organization?.type || "operation";
  const timeZone = organization?.timezone || moment.tz.guess();
  const [allMembers, setAllMembers] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState({});
  const [startJobId, setStartJobId] = useState(null);
  const [endJobId, setEndJobId] = useState(null);

  // Fetch members on mount
  useEffect(() => {
    fetchMembers();
  }, [organizationId]);

  // Fetch jobs when assignees are selected
  useEffect(() => {
    if (selectedAssignees.length > 0) {
      fetchJobsForAssignees();
    } else {
      setJobs([]);
      setSelectedJobs({});
    }
  }, [selectedAssignees, selectedDate]);

  const fetchMembers = async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
      const membersUrl = `${organizationId}/members/short/`;
      const membersResponse = await defaultApi(membersUrl, 'GET');

      // Extract members array from response
      let membersList = [];
      
      // Check if it's an Axios response object (has data property)
      if (membersResponse?.data) {
        // Check if data.results is an array (paginated response)
        if (Array.isArray(membersResponse.data.results)) {
          membersList = membersResponse.data.results;
        }
        // Check if data itself is an array
        else if (Array.isArray(membersResponse.data)) {
          membersList = membersResponse.data;
        }
      }
      // Direct array response
      else if (Array.isArray(membersResponse)) {
        membersList = membersResponse;
      }
      // Object with results property
      else if (Array.isArray(membersResponse?.results)) {
        membersList = membersResponse.results;
      }

      setAllMembers(membersList);

      // Create options for the dropdown
      const options = membersList.map(member => {
        const userId = member.user?.id || member.id;
        const memberName = member.user 
          ? `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || member.user.email
          : (member.full_name || member.name || member.email);
        
        return {
          value: userId,
          label: memberName,
        };
      });
      setMemberOptions(options);
    } catch (error) {
      toast.error('Failed to load members');
      setAllMembers([]);
      setMemberOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsForAssignees = async () => {
    if (!organizationId || selectedAssignees.length === 0) return;

    setLoadingJobs(true);
    try {
      // Fetch jobs for each selected assignee
      const jobsPromises = selectedAssignees.map(userId => {
        const jobsUrl = `${organizationId}/workorder/job_work_order_schedules/?paginate=true&start_time=${selectedDate}&end_time=${selectedDate}&assignee_id=${userId}`;
        return defaultApi(jobsUrl, 'GET');
      });

      const jobsResponses = await Promise.all(jobsPromises);

      // Combine all jobs from all assignees
      let allJobs = [];
      jobsResponses.forEach((jobsResponse) => {
        let jobsList = [];
        
        // Check if it's an Axios response object (has data property)
        if (jobsResponse?.data) {
          // Check if data.results is an array (paginated response)
          if (Array.isArray(jobsResponse.data.results)) {
            jobsList = jobsResponse.data.results;
          }
          // Check if data itself is an array
          else if (Array.isArray(jobsResponse.data)) {
            jobsList = jobsResponse.data;
          }
        }
        // Direct array response (no axios wrapper)
        else if (Array.isArray(jobsResponse)) {
          jobsList = jobsResponse;
        }
        // Object with results property
        else if (jobsResponse?.results && Array.isArray(jobsResponse.results)) {
          jobsList = jobsResponse.results;
        }
        
        allJobs = [...allJobs, ...jobsList];
      });

      setJobs(allJobs);

      // Initialize selectedJobs structure for selected assignees
      const initialSelection = {};
      selectedAssignees.forEach(userId => {
        initialSelection[userId] = [];
      });
      setSelectedJobs(initialSelection);

    } catch (error) {
      toast.error('Failed to load jobs');
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Get jobs for a specific member (using user.id)
  const getMemberJobs = (userId) => {
    // Ensure jobs is an array
    if (!Array.isArray(jobs)) {
      return [];
    }
    
    const filteredJobs = jobs.filter(job => {
      // Check if this user is assigned to this job
      // In the new API, assignees is an array of user objects with { id, first_name, last_name, ... }
      if (Array.isArray(job.assignees)) {
        const isAssigned = job.assignees.some(assignee => {
          return assignee.id === userId;
        });
        return isAssigned;
      }
      return false;
    });
    
    return filteredJobs;
  };

  // Handle assignee dropdown change
  const handleAssigneeChange = (selectedOptions) => {
    if (selectedOptions && selectedOptions.length > 0) {
      const userIds = selectedOptions.map(option => option.value);
      setSelectedAssignees(userIds);
    } else {
      setSelectedAssignees([]);
    }
  };

  // Toggle job selection
  const toggleJob = (employeeId, jobId) => {
    setSelectedJobs(prev => {
      const empJobs = prev[employeeId] || [];
      const isSelected = empJobs.includes(jobId);
      
      return {
        ...prev,
        [employeeId]: isSelected
          ? empJobs.filter(id => id !== jobId)
          : [...empJobs, jobId]
      };
    });
  };

  // Select all jobs for a member
  const selectAllForMember = (memberId) => {
    const memberJobs = getMemberJobs(memberId);
    const allJobIds = memberJobs.map(job => job.id);
    
    setSelectedJobs(prev => ({
      ...prev,
      [memberId]: allJobIds
    }));
  };

  // Deselect all jobs for a member
  const deselectAllForMember = (memberId) => {
    setSelectedJobs(prev => ({
      ...prev,
      [memberId]: []
    }));
  };

  // Calculate total selected jobs
  const getTotalSelected = () => {
    return Object.values(selectedJobs).reduce((sum, jobs) => sum + jobs.length, 0);
  };

  // Get all selected jobs with their details for dropdown
  const getAllSelectedJobsOptions = () => {
    const allSelectedJobIds = Object.values(selectedJobs).flat();
    return jobs
      .filter(job => allSelectedJobIds.includes(job.id))
      .map(job => ({
        value: job.id,
        label: job.name || job.work_order?.name || `Job #${job.job_code || job.id}`
      }));
  };

  // Handle optimize
  const handleOptimize = () => {
    const totalSelected = getTotalSelected();
    
    if (totalSelected === 0) {
      toast.error('Please select at least one job to optimize');
      return;
    }

    // Validate start and end job selections
    if (startJobId) {
      // Check if start job is in selected jobs
      const isStartJobSelected = Object.values(selectedJobs).some(jobIds => 
        jobIds.includes(startJobId)
      );
      if (!isStartJobSelected) {
        toast.error('Start job must be one of the selected jobs');
        return;
      }
    }

    if (endJobId) {
      // Check if end job is in selected jobs
      const isEndJobSelected = Object.values(selectedJobs).some(jobIds => 
        jobIds.includes(endJobId)
      );
      if (!isEndJobSelected) {
        toast.error('End job must be one of the selected jobs');
        return;
      }

      // Check if start and end are the same
      if (startJobId && startJobId === endJobId) {
        toast.error('Start and end jobs cannot be the same');
        return;
      }
    }

    // Filter out members with no selected jobs
    const membersWithJobs = Object.entries(selectedJobs)
      .filter(([_, jobs]) => jobs.length > 0)
      .map(([memberId, jobIds]) => ({
        employee_id: memberId,
        job_ids: jobIds
      }));

    // Pass start and end job IDs if selected
    onOptimize(membersWithJobs, startJobId, endJobId);
    onClose();
  };

  if (loading) {
    return (
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{orgType === "operation" ? "Select Tasks to Optimize" : "Select Jobs to Optimize"}</ModalTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
          <ComponentCircleLoader />
        </ModalContent>
      </ModalOverlay>
    );
  }

  if (!allMembers || allMembers.length === 0) {
    return (
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{orgType === "operation" ? "Select Tasks to Optimize" : "Select Jobs to Optimize"}</ModalTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
          <EmptyState>
            No members found. Please add members to your organization first.
          </EmptyState>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // Get filtered members based on selected assignees
  const displayMembers = allMembers.filter(member => {
    const userId = member.user?.id || member.id;
    return selectedAssignees.includes(userId);
  });

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{orgType === "operation" ? "Select Tasks to Optimize" : "Select Jobs to Optimize"}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <AssigneeSelectionSection>
          <FilterLabel>Select Assignees</FilterLabel>
          <Select
            isMulti
            isSearchable
            value={memberOptions.filter(option => selectedAssignees.includes(option.value))}
            options={memberOptions}
            placeholder="Select assignees..."
            onChange={handleAssigneeChange}
            styles={dropdownStyles}
            filterOption={filterOption}
            components={{
              IndicatorSeparator: () => null,
              ClearIndicator: props => {
                return <ClearFilterButton {...props} />;
              },
            }}
          />
        </AssigneeSelectionSection>

        {getTotalSelected() > 0 && (
          <RoutePointsSection>
            <RoutePointsTitle>🚩 Route Start & End Points (Optional)</RoutePointsTitle>
            <RoutePointsGrid>
              <RoutePointField>
                <RoutePointLabel>Start Point</RoutePointLabel>
                <Select
                  isClearable
                  isSearchable
                  value={getAllSelectedJobsOptions().find(opt => opt.value === startJobId) || null}
                  options={getAllSelectedJobsOptions()}
                  placeholder={`Select start ${orgType === "operation" ? "task" : "job"}...`}
                  onChange={(option) => setStartJobId(option ? option.value : null)}
                  styles={dropdownStyles}
                  filterOption={filterOption}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                />
                <RoutePointHint>The route will begin from this {orgType === "operation" ? "task" : "job"}</RoutePointHint>
              </RoutePointField>
              
              <RoutePointField>
                <RoutePointLabel>End Point (Optional)</RoutePointLabel>
                <Select
                  isClearable
                  isSearchable
                  value={getAllSelectedJobsOptions().find(opt => opt.value === endJobId) || null}
                  options={getAllSelectedJobsOptions().filter(opt => opt.value !== startJobId)}
                  placeholder={`Select end ${orgType === "operation" ? "task" : "job"}...`}
                  onChange={(option) => setEndJobId(option ? option.value : null)}
                  styles={dropdownStyles}
                  filterOption={filterOption}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                />
                <RoutePointHint>The route will end at this {orgType === "operation" ? "task" : "job"}</RoutePointHint>
              </RoutePointField>
            </RoutePointsGrid>
          </RoutePointsSection>
        )}

        {loadingJobs && (
          <EmptyState>
            <ComponentCircleLoader />
            <p>{orgType === "operation" ? "Loading tasks for selected assignees..." : "Loading jobs for selected assignees..."}</p>
          </EmptyState>
        )}

        {!loadingJobs && selectedAssignees.length === 0 && (
          <EmptyState>
            Please select at least one assignee to view their {orgType === "operation" ? "tasks" : "jobs"}.
          </EmptyState>
        )}

        {!loadingJobs && selectedAssignees.length > 0 && (!jobs || jobs.length === 0) && (
          <EmptyState>
            No {orgType === "operation" ? "tasks" : "jobs"} found for selected assignees on {selectedDate}.
          </EmptyState>
        )}

        {!loadingJobs && displayMembers.map(member => {
          const userId = member.user?.id || member.id;
          const memberName = member.user 
            ? `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || member.user.email
            : (member.full_name || member.name || member.email);
          
          const memberJobs = getMemberJobs(userId);
          const selectedCount = (selectedJobs[userId] || []).length;
          const allSelected = selectedCount === memberJobs.length && memberJobs.length > 0;

          if (memberJobs.length === 0) return null;

          return (
            <EmployeeSection key={userId}>
              <EmployeeHeader>
                <EmployeeName>
                  {memberName} ({selectedCount}/{memberJobs.length} selected)
                </EmployeeName>
                <SelectAllButton
                  onClick={() =>
                    allSelected
                      ? deselectAllForMember(userId)
                      : selectAllForMember(userId)
                  }
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </SelectAllButton>
              </EmployeeHeader>

              <JobsList>
                {memberJobs.map(job => {
                  const isSelected = (selectedJobs[userId] || []).includes(job.id);
                  const jobAddress = job.location?.address || 'No address';
                  const jobName = job.name || job.work_order?.name || `Job #${job.job_code || job.id}`;
                  
                  return (
                    <JobItem key={job.id}>
                      <Checkbox
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleJob(userId, job.id)}
                      />
                      <JobInfo>
                        <JobName>{jobName}</JobName>
                        <JobAddress>{jobAddress}</JobAddress>
                      </JobInfo>
                    </JobItem>
                  );
                })}
              </JobsList>
            </EmployeeSection>
          );
        })}

        <ModalFooter>
          <SelectionSummary>
            {getTotalSelected()} {orgType === "operation" ? "task" : "job"}{getTotalSelected() !== 1 ? 's' : ''} selected
          </SelectionSummary>
          <ButtonGroup>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleOptimize}>
              Optimize Selected {orgType === "operation" ? "Tasks" : "Jobs"}
            </PrimaryButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}

export default JobSelectionModal;

